import { useState, type MouseEvent } from 'react';
import { BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAnalytics } from '@/core/analytics';
import { openExternalUrl } from '../services/feedbackApi';

const DOCS_LABEL = 'View documentation';

type DocsLinkButtonProps = {
  /** Absolute docs URL to open in the system browser. */
  url: string;
  /** Low-cardinality doc identifier for analytics (no PII). */
  doc: 'loose-foreign-keys' | 'json-columns' | 'enum-columns' | 'database-blueprint' | 'spatial';
};

/**
 * Small icon-button rendered next to a dialog title that opens the relevant
 * public documentation page in the system browser.
 *
 * Note: the app shell already mounts a global TooltipProvider, but this
 * component intentionally brings its own. Radix's Tooltip throws if no
 * TooltipProvider is in context at render time, so a local provider keeps the
 * button renderable in isolation (unit tests, dialogs rendered outside the
 * app tree) without coupling to a host-supplied provider. Nested providers are
 * harmless in Radix.
 */
export function DocsLinkButton({ url, doc }: DocsLinkButtonProps) {
  const { track } = useAnalytics();
  // Controlled by hover only. Radix Tooltip otherwise also opens on *focus*,
  // and Radix Dialog autofocuses its first focusable child on open — so an
  // uncontrolled tooltip pops by itself the moment a dialog mounts. Driving
  // `open` purely from pointer enter/leave keeps it hover-only (the
  // `aria-label` still covers keyboard / screen-reader users).
  const [open, setOpen] = useState(false);

  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    track('feature:docs_open', { doc });
    void openExternalUrl(url);
  };

  return (
    <TooltipProvider>
      <Tooltip open={open}>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-foreground"
            onClick={handleClick}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            aria-label={DOCS_LABEL}
            data-testid="docs-link-btn"
          >
            <BookOpen className="h-3.5 w-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{DOCS_LABEL}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
